import Phaser from "phaser";
import "./style.css";
import {
  CHANNEL_COLORS,
  CHANNEL_CSS,
  CHANNELS,
  DIRECTION_VECTORS,
  LEVELS,
  TILE_KINDS,
  cloneTiles,
  getConnections,
  traceNetwork,
  type Direction,
  type LevelDefinition,
  type NetworkResult,
  type PacketChannel,
  type PacketRoute,
  type SavedLevel,
  type TileKind,
  type TileState
} from "./model";

type EditorTool = TileKind | "erase";

interface BoardLayout {
  tileSize: number;
  gap: number;
  left: number;
  top: number;
}

const GAME_WIDTH = 900;
const GAME_HEIGHT = 600;

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Packet Pal interface is missing ${selector}.`);
  return element;
}

const movesElement = requiredElement<HTMLElement>("#moves");
const attemptsElement = requiredElement<HTMLElement>("#attempts");
const statusElement = requiredElement<HTMLElement>("#status");
const levelLabelElement = requiredElement<HTMLElement>("#level-label");
const instructionsElement = requiredElement<HTMLElement>("#instructions");
const levelSelect = requiredElement<HTMLSelectElement>("#level-select");
const sendButton = requiredElement<HTMLButtonElement>("#send");
const resetButton = requiredElement<HTMLButtonElement>("#reset");
const editorButton = requiredElement<HTMLButtonElement>("#open-editor");
const winDialog = requiredElement<HTMLDialogElement>("#win-dialog");
const winSummary = requiredElement<HTMLElement>("#win-summary");
const playAgainButton = requiredElement<HTMLButtonElement>("#play-again");
const nextLevelButton = requiredElement<HTMLButtonElement>("#next-level");
const editorDialog = requiredElement<HTMLDialogElement>("#editor-dialog");
const editorTitle = requiredElement<HTMLInputElement>("#editor-title");
const editorColumnsSelect = requiredElement<HTMLSelectElement>("#editor-columns");
const editorRowsSelect = requiredElement<HTMLSelectElement>("#editor-rows");
const editorGrid = requiredElement<HTMLElement>("#editor-grid");
const editorStatus = requiredElement<HTMLElement>("#editor-status");
const editorFixed = requiredElement<HTMLInputElement>("#editor-fixed");
const channelPalette = requiredElement<HTMLElement>("#channel-palette");
const tilePalette = requiredElement<HTMLElement>("#tile-palette");
const levelData = requiredElement<HTMLTextAreaElement>("#level-data");
const exportLevelButton = requiredElement<HTMLButtonElement>("#export-level");
const importLevelButton = requiredElement<HTMLButtonElement>("#import-level");
const clearEditorButton = requiredElement<HTMLButtonElement>("#clear-editor");
const loadCurrentButton = requiredElement<HTMLButtonElement>("#load-current");
const saveLevelButton = requiredElement<HTMLButtonElement>("#save-level");
const playCustomButton = requiredElement<HTMLButtonElement>("#play-custom");

const boardLayout = (columns: number, rows: number): BoardLayout => {
  const gap = columns >= 9 || rows >= 7 ? 7 : 10;
  const tileSize = Math.floor(
    Math.min(
      82,
      (800 - (columns - 1) * gap) / columns,
      (470 - (rows - 1) * gap) / rows
    )
  );
  const width = columns * tileSize + (columns - 1) * gap;
  const height = rows * tileSize + (rows - 1) * gap;
  return {
    tileSize,
    gap,
    left: (GAME_WIDTH - width) / 2,
    top: (GAME_HEIGHT - height) / 2
  };
};

class PacketPalScene extends Phaser.Scene {
  private currentLevel: LevelDefinition = LEVELS[0];
  private builtInIndex: number | null = 0;
  private tiles: TileState[] = [];
  private layout: BoardLayout = boardLayout(LEVELS[0].columns, LEVELS[0].rows);
  private boardGraphics!: Phaser.GameObjects.Graphics;
  private interactiveObjects!: Phaser.GameObjects.Group;
  private packetSprites: Phaser.GameObjects.Arc[] = [];
  private attemptRotations = new Map<string, number>();
  private moves = 0;
  private attempts = 0;
  private locked = false;
  private completed = false;

  constructor() {
    super("PacketPal");
  }

  create(): void {
    this.cameras.main.setBackgroundColor("#0b1417");
    this.drawBackground();
    this.boardGraphics = this.add.graphics();
    this.interactiveObjects = this.add.group();
    this.loadBuiltInLevel(0);
    this.input.keyboard?.on("keydown-ENTER", () => this.sendPackets());
    this.input.keyboard?.on("keydown-R", () => this.resetLevel());
  }

  loadBuiltInLevel(index: number): void {
    this.builtInIndex = Phaser.Math.Clamp(index, 0, LEVELS.length - 1);
    this.currentLevel = LEVELS[this.builtInIndex];
    this.prepareLevel();
  }

  loadCustomLevel(level: LevelDefinition): void {
    this.builtInIndex = null;
    this.currentLevel = {
      ...level,
      id: "custom",
      tiles: level.tiles.map((item) => ({
        ...item,
        initialRotation: item.rotation
      }))
    };
    this.prepareLevel();
  }

  resetLevel(): void {
    if (!this.locked) this.prepareLevel();
  }

  sendPackets(): void {
    if (this.locked || this.completed) return;
    this.attemptRotations = new Map(
      this.tiles
        .filter((item) => item.kind === "turnstile")
        .map((item) => [`${item.col}:${item.row}`, item.rotation])
    );
    const result = traceNetwork(
      this.tiles,
      this.currentLevel.columns,
      this.currentLevel.rows
    );
    if (result.routes.length === 0) {
      this.updateHud(result.reason);
      return;
    }

    this.attempts += 1;
    this.locked = true;
    this.setButtonsDisabled(true);
    this.updateHud(
      result.routes.length === 1 ? "Packet in transit…" : "Packets in transit…"
    );
    this.clearPackets();

    const makeSprite = (route: PacketRoute): Phaser.GameObjects.Arc => {
      const first = route.points[0];
      const point = this.tileCenter(first.col, first.row);
      const sprite = this.add
        .circle(point.x, point.y, Math.max(6, this.layout.tileSize * 0.1), CHANNEL_COLORS[route.channel])
        .setStrokeStyle(3, 0xffffff, 0.32)
        .setDepth(20);
      this.packetSprites.push(sprite);
      return sprite;
    };

    const usesTurnstile = result.routes.some((route) =>
      route.points.some((point) => point.rotationAfter !== undefined)
    );
    if (usesTurnstile) {
      const animateNext = (routeIndex: number): void => {
        if (routeIndex >= result.routes.length) {
          this.finishAttempt(result);
          return;
        }
        const route = result.routes[routeIndex];
        this.animatePacket(route, makeSprite(route), 1, () =>
          animateNext(routeIndex + 1)
        );
      };
      animateNext(0);
      return;
    }

    let finished = 0;
    for (const route of result.routes) {
      this.animatePacket(route, makeSprite(route), 1, () => {
        finished += 1;
        if (finished === result.routes.length) this.finishAttempt(result);
      });
    }
  }

  getCurrentLevelForEditor(): SavedLevel {
    return {
      title: this.currentLevel.title,
      columns: this.currentLevel.columns,
      rows: this.currentLevel.rows,
      tiles: this.tiles.map((item) => ({
        ...item,
        initialRotation: item.rotation,
        solutionRotation: undefined
      }))
    };
  }

  getBuiltInIndex(): number | null {
    return this.builtInIndex;
  }

  private prepareLevel(): void {
    this.tweens.killAll();
    this.clearPackets();
    this.tiles = cloneTiles(this.currentLevel.tiles);
    this.layout = boardLayout(this.currentLevel.columns, this.currentLevel.rows);
    this.moves = 0;
    this.attempts = 0;
    this.completed = false;
    this.locked = false;
    this.attemptRotations.clear();
    this.setButtonsDisabled(false);
    this.updateLevelInterface();
    this.updateHud(
      this.tiles.filter((item) => item.kind === "source").length > 1
        ? "Route every client to its matching server."
        : "Find a valid route to the matching server."
    );
    this.drawBoard();
  }

  private clearPackets(): void {
    for (const sprite of this.packetSprites) sprite.destroy();
    this.packetSprites = [];
  }

  private drawBackground(): void {
    const graphics = this.add.graphics();
    graphics.lineStyle(1, 0x183036, 0.5);
    for (let x = 0; x <= GAME_WIDTH; x += 30) graphics.lineBetween(x, 0, x, GAME_HEIGHT);
    for (let y = 0; y <= GAME_HEIGHT; y += 30) graphics.lineBetween(0, y, GAME_WIDTH, y);
    graphics.fillStyle(0x62d7e8, 0.03);
    graphics.fillCircle(110, 500, 190);
    graphics.fillStyle(0xb79aff, 0.025);
    graphics.fillCircle(790, 80, 220);
  }

  private drawBoard(): void {
    this.boardGraphics.clear();
    this.interactiveObjects.clear(true, true);
    this.input.removeAllListeners("gameobjectdown");

    for (let row = 0; row < this.currentLevel.rows; row += 1) {
      for (let col = 0; col < this.currentLevel.columns; col += 1) {
        const { x, y } = this.tileCenter(col, row);
        const size = this.layout.tileSize;
        this.boardGraphics.fillStyle(0x101d21, 0.66);
        this.boardGraphics.fillRoundedRect(
          x - size / 2,
          y - size / 2,
          size,
          size,
          size * 0.14
        );
        this.boardGraphics.lineStyle(1, 0x294047, 0.45);
        this.boardGraphics.strokeRoundedRect(
          x - size / 2,
          y - size / 2,
          size,
          size,
          size * 0.14
        );
      }
    }

    for (const item of this.tiles) {
      this.drawTile(item);
      if (!item.fixed) {
        const { x, y } = this.tileCenter(item.col, item.row);
        const zone = this.add
          .zone(x, y, this.layout.tileSize, this.layout.tileSize)
          .setInteractive({ useHandCursor: true })
          .setData("tile", item);
        this.interactiveObjects.add(zone);
      }
    }

    this.input.on(
      "gameobjectdown",
      (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject) => {
        const item = gameObject.getData("tile") as TileState | undefined;
        if (item) this.rotateTile(item, pointer.rightButtonDown() ? -1 : 1);
      }
    );
  }

  private drawTile(item: TileState): void {
    const { x, y } = this.tileCenter(item.col, item.row);
    const size = this.layout.tileSize;
    const scale = size / 82;
    const channelColor = CHANNEL_COLORS[item.channel ?? "cyan"];
    const wireColor =
      item.kind === "firewall"
        ? 0xff9a68
        : item.kind === "turnstile"
          ? 0xffd76a
        : item.kind === "encryptor"
          ? 0xb79aff
          : item.kind === "filter"
            ? channelColor
            : 0x62d7e8;

    this.drawConnections(x, y, getConnections(item), wireColor);

    if (item.kind === "source") {
      this.drawEndpointTile(x, y, channelColor, false);
      const clients = this.tiles.filter((candidate) => candidate.kind === "source");
      const orderMatters = this.tiles.some(
        (candidate) => candidate.kind === "turnstile"
      );
      if (orderMatters && clients.length > 1) {
        const order = clients.indexOf(item) + 1;
        const label = this.add
          .text(x + size * 0.3, y - size * 0.32, String(order), {
            color: "#0b1417",
            backgroundColor: "#ffd76a",
            fontFamily: "monospace",
            fontSize: `${Math.max(12, Math.round(size * 0.2))}px`,
            fontStyle: "bold",
            padding: { x: 4, y: 1 }
          })
          .setOrigin(0.5)
          .setDepth(12);
        this.interactiveObjects.add(label);
      }
    } else if (item.kind === "server") {
      this.drawEndpointTile(x, y, channelColor, true);
    } else if (item.kind === "router") {
      this.drawRelay(x, y, 0x62d7e8, 0.25);
      this.drawArrow(x, y, item.rotation as Direction, 0xffd76a);
    } else if (item.kind === "turnstile") {
      this.drawTurnstile(x, y, item.rotation as Direction);
    } else if (item.kind === "oneway") {
      this.drawRelay(x, y, 0x62d7e8, 0.22);
      this.drawArrow(x, y, ((1 + item.rotation) % 4) as Direction, 0xffd76a);
    } else if (item.kind === "cross") {
      this.boardGraphics.fillStyle(0x142c32, 1);
      this.boardGraphics.fillCircle(x, y, 16 * scale);
      this.boardGraphics.lineStyle(2.5 * scale, 0x83f1c1, 1);
      this.boardGraphics.strokeCircle(x, y, 16 * scale);
      this.boardGraphics.fillStyle(0x83f1c1, 1);
      this.boardGraphics.fillCircle(x, y, 4.5 * scale);
    } else if (item.kind === "hub") {
      this.boardGraphics.fillStyle(0x3a271c, 1);
      this.boardGraphics.fillRoundedRect(x - 17 * scale, y - 17 * scale, 34 * scale, 34 * scale, 7 * scale);
      this.boardGraphics.lineStyle(2.5 * scale, 0xff9a68, 1);
      this.boardGraphics.strokeRoundedRect(x - 17 * scale, y - 17 * scale, 34 * scale, 34 * scale, 7 * scale);
      this.boardGraphics.fillStyle(0xffc866, 1);
      this.boardGraphics.fillCircle(x, y, 5 * scale);
    } else if (item.kind === "encryptor") {
      this.drawLock(x, y, 0xb79aff, 0x241f3d);
    } else if (item.kind === "firewall") {
      this.drawFirewall(x, y);
    } else if (item.kind === "filter") {
      this.boardGraphics.fillStyle(0x142229, 1);
      this.boardGraphics.fillCircle(x, y, 17 * scale);
      this.boardGraphics.lineStyle(3 * scale, channelColor, 1);
      this.boardGraphics.strokeCircle(x, y, 17 * scale);
      this.boardGraphics.beginPath();
      this.boardGraphics.moveTo(x - 8 * scale, y - 7 * scale);
      this.boardGraphics.lineTo(x + 8 * scale, y - 7 * scale);
      this.boardGraphics.lineTo(x + 3 * scale, y + 8 * scale);
      this.boardGraphics.lineTo(x - 3 * scale, y + 8 * scale);
      this.boardGraphics.closePath();
      this.boardGraphics.strokePath();
    } else {
      this.drawRelay(x, y, 0x62d7e8, 0.2);
    }

  }

  private drawConnections(
    x: number,
    y: number,
    connections: Direction[],
    color: number
  ): void {
    const size = this.layout.tileSize;
    this.boardGraphics.lineStyle(Math.max(8, size * 0.16), 0x20363b, 1);
    for (const direction of connections) {
      const end = this.connectionEnd(x, y, direction);
      this.boardGraphics.lineBetween(x, y, end.x, end.y);
    }
    this.boardGraphics.lineStyle(Math.max(3, size * 0.05), color, 0.92);
    for (const direction of connections) {
      const end = this.connectionEnd(x, y, direction);
      this.boardGraphics.lineBetween(x, y, end.x, end.y);
      this.boardGraphics.fillStyle(color, 1);
      this.boardGraphics.fillCircle(end.x, end.y, Math.max(2.5, size * 0.045));
    }
  }

  private drawEndpointTile(x: number, y: number, color: number, server: boolean): void {
    const size = this.layout.tileSize;
    const scale = size / 82;
    this.boardGraphics.fillStyle(server ? 0x10211d : 0x11252a, 1);
    this.boardGraphics.fillRoundedRect(x - size * 0.43, y - size * 0.43, size * 0.86, size * 0.86, size * 0.14);
    this.boardGraphics.lineStyle(Math.max(2, size * 0.035), color, 1);
    this.boardGraphics.strokeRoundedRect(x - size * 0.43, y - size * 0.43, size * 0.86, size * 0.86, size * 0.14);

    if (server) {
      this.boardGraphics.fillStyle(0x0b1715, 1);
      this.boardGraphics.fillRoundedRect(x - 17 * scale, y - 24 * scale, 34 * scale, 48 * scale, 5 * scale);
      this.boardGraphics.lineStyle(2.5 * scale, color, 1);
      this.boardGraphics.strokeRoundedRect(x - 17 * scale, y - 24 * scale, 34 * scale, 48 * scale, 5 * scale);
      for (const offset of [-12, 0, 12]) {
        this.boardGraphics.lineBetween(x - 11 * scale, y + offset * scale, x + 11 * scale, y + offset * scale);
        this.boardGraphics.fillStyle(0xffd76a, 1);
        this.boardGraphics.fillCircle(x + 8 * scale, y + (offset - 4) * scale, 1.8 * scale);
      }
    } else {
      this.boardGraphics.fillStyle(0x0b171a, 1);
      this.boardGraphics.fillRoundedRect(x - 21 * scale, y - 17 * scale, 42 * scale, 29 * scale, 5 * scale);
      this.boardGraphics.lineStyle(2.5 * scale, color, 1);
      this.boardGraphics.strokeRoundedRect(x - 21 * scale, y - 17 * scale, 42 * scale, 29 * scale, 5 * scale);
      this.boardGraphics.lineBetween(x, y + 12 * scale, x, y + 20 * scale);
      this.boardGraphics.lineBetween(x - 11 * scale, y + 21 * scale, x + 11 * scale, y + 21 * scale);
    }
  }

  private drawRelay(x: number, y: number, color: number, alpha: number): void {
    const scale = this.layout.tileSize / 82;
    this.boardGraphics.fillStyle(0x142c32, 1);
    this.boardGraphics.fillRoundedRect(x - 17 * scale, y - 17 * scale, 34 * scale, 34 * scale, 8 * scale);
    this.boardGraphics.lineStyle(2.2 * scale, color, 0.85);
    this.boardGraphics.strokeRoundedRect(x - 17 * scale, y - 17 * scale, 34 * scale, 34 * scale, 8 * scale);
    this.boardGraphics.fillStyle(0x83f1c1, 1);
    this.boardGraphics.fillCircle(x - 6 * scale, y, 2.5 * scale);
    this.boardGraphics.fillCircle(x + 6 * scale, y, 2.5 * scale);
    this.boardGraphics.fillStyle(color, alpha);
    this.boardGraphics.fillCircle(x, y, 13 * scale);
  }

  private drawArrow(x: number, y: number, direction: Direction, color: number): void {
    const scale = this.layout.tileSize / 82;
    const vector = DIRECTION_VECTORS[direction];
    const perpendicular = { col: -vector.row, row: vector.col };
    const tipX = x + vector.col * 11 * scale;
    const tipY = y + vector.row * 11 * scale;
    const backX = x - vector.col * 8 * scale;
    const backY = y - vector.row * 8 * scale;
    this.boardGraphics.lineStyle(3 * scale, color, 1);
    this.boardGraphics.lineBetween(backX, backY, tipX, tipY);
    this.boardGraphics.lineBetween(
      tipX,
      tipY,
      tipX - vector.col * 7 * scale + perpendicular.col * 5 * scale,
      tipY - vector.row * 7 * scale + perpendicular.row * 5 * scale
    );
    this.boardGraphics.lineBetween(
      tipX,
      tipY,
      tipX - vector.col * 7 * scale - perpendicular.col * 5 * scale,
      tipY - vector.row * 7 * scale - perpendicular.row * 5 * scale
    );
  }

  private drawTurnstile(x: number, y: number, direction: Direction): void {
    const scale = this.layout.tileSize / 82;
    this.drawRelay(x, y, 0xffd76a, 0.22);
    this.boardGraphics.lineStyle(2.2 * scale, 0xffd76a, 0.7);
    this.boardGraphics.beginPath();
    this.boardGraphics.arc(x, y, 14 * scale, Math.PI * 0.15, Math.PI * 1.55);
    this.boardGraphics.strokePath();
    this.boardGraphics.fillStyle(0xffd76a, 1);
    this.boardGraphics.fillTriangle(
      x - 4 * scale,
      y - 15 * scale,
      x + 4 * scale,
      y - 19 * scale,
      x + 3 * scale,
      y - 10 * scale
    );
    this.drawArrow(x, y, direction, 0xffd76a);
  }

  private drawLock(x: number, y: number, color: number, fill: number): void {
    const scale = this.layout.tileSize / 82;
    this.boardGraphics.fillStyle(fill, 1);
    this.boardGraphics.fillRoundedRect(x - 17 * scale, y - 16 * scale, 34 * scale, 32 * scale, 7 * scale);
    this.boardGraphics.lineStyle(2.5 * scale, color, 1);
    this.boardGraphics.strokeRoundedRect(x - 17 * scale, y - 16 * scale, 34 * scale, 32 * scale, 7 * scale);
    this.boardGraphics.strokeRoundedRect(x - 7 * scale, y - 3 * scale, 14 * scale, 12 * scale, 3 * scale);
    this.boardGraphics.beginPath();
    this.boardGraphics.arc(x, y - 3 * scale, 6 * scale, Math.PI, 0);
    this.boardGraphics.strokePath();
  }

  private drawFirewall(x: number, y: number): void {
    const scale = this.layout.tileSize / 82;
    this.boardGraphics.fillStyle(0x3a241d, 1);
    this.boardGraphics.fillRoundedRect(x - 19 * scale, y - 19 * scale, 38 * scale, 38 * scale, 7 * scale);
    this.boardGraphics.lineStyle(2.3 * scale, 0xff9a68, 1);
    this.boardGraphics.strokeRoundedRect(x - 19 * scale, y - 19 * scale, 38 * scale, 38 * scale, 7 * scale);
    this.boardGraphics.lineBetween(x - 13 * scale, y - 6 * scale, x + 13 * scale, y - 6 * scale);
    this.boardGraphics.lineBetween(x - 13 * scale, y + 6 * scale, x + 13 * scale, y + 6 * scale);
    this.boardGraphics.lineBetween(x, y - 17 * scale, x, y + 17 * scale);
  }

  private rotateTile(item: TileState, step: -1 | 1): void {
    if (this.locked || this.completed) return;
    item.rotation = (item.rotation + step + 4) % 4;
    this.moves += 1;
    this.updateHud("Network changed. Send when ready.");
    this.drawBoard();
  }

  private animatePacket(
    route: PacketRoute,
    sprite: Phaser.GameObjects.Arc,
    index: number,
    done: () => void
  ): void {
    if (index >= route.points.length) {
      done();
      return;
    }
    const routePoint = route.points[index];
    const point = this.tileCenter(routePoint.col, routePoint.row);
    this.tweens.add({
      targets: sprite,
      x: point.x,
      y: point.y,
      duration: 290,
      ease: "Sine.InOut",
      onComplete: () => {
        sprite.setStrokeStyle(
          routePoint.encrypted ? 4 : 3,
          routePoint.encrypted ? 0xffffff : 0xffffff,
          routePoint.encrypted ? 0.85 : 0.32
        );
        const departedPoint = route.points[index - 1];
        if (departedPoint.rotationAfter !== undefined) {
          const turnstile = this.tiles.find(
            (item) =>
              item.kind === "turnstile" &&
              item.col === departedPoint.col &&
              item.row === departedPoint.row
          );
          if (turnstile) {
            turnstile.rotation = departedPoint.rotationAfter;
            this.drawBoard();
          }
        }
        this.animatePacket(route, sprite, index + 1, done);
      }
    });
  }

  private finishAttempt(result: NetworkResult): void {
    for (const item of this.tiles) {
      if (item.kind !== "turnstile") continue;
      const initial = this.attemptRotations.get(`${item.col}:${item.row}`);
      if (initial !== undefined) item.rotation = initial;
    }
    this.drawBoard();

    if (result.success) {
      this.completed = true;
      for (const sprite of this.packetSprites) {
        this.tweens.add({ targets: sprite, scale: 2, alpha: 0, duration: 360, ease: "Quad.Out" });
      }
      this.time.delayedCall(380, () => this.showWin());
      return;
    }

    this.cameras.main.shake(170, 0.004);
    for (const sprite of this.packetSprites) {
      sprite.setFillStyle(0xff8b71, 1);
      this.tweens.add({ targets: sprite, alpha: 0, scale: 0.3, duration: 260 });
    }
    this.time.delayedCall(280, () => {
      this.clearPackets();
      this.locked = false;
      this.setButtonsDisabled(false);
      this.updateHud(result.reason);
    });
  }

  private showWin(): void {
    this.locked = false;
    this.setButtonsDisabled(false);
    this.updateHud("All packets delivered.");
    winSummary.textContent = `${this.currentLevel.title} completed after ${this.attempts} ${
      this.attempts === 1 ? "network test" : "network tests"
    }.`;
    nextLevelButton.hidden =
      this.builtInIndex === null || this.builtInIndex >= LEVELS.length - 1;
    winDialog.showModal();
  }

  private updateLevelInterface(): void {
    if (this.builtInIndex === null) {
      levelLabelElement.textContent = `Custom · ${this.currentLevel.columns}×${this.currentLevel.rows}`;
      instructionsElement.textContent = this.currentLevel.tip;
      if (!levelSelect.querySelector('option[value="custom"]')) {
        const option = document.createElement("option");
        option.value = "custom";
        option.textContent = "Custom · Editor";
        levelSelect.append(option);
      }
      levelSelect.value = "custom";
      return;
    }

    levelSelect.querySelector('option[value="custom"]')?.remove();
    const number = this.builtInIndex + 1;
    levelLabelElement.textContent = `Level ${number} of ${LEVELS.length} · ${this.currentLevel.title}`;
    instructionsElement.textContent = this.currentLevel.tip;
    levelSelect.value = String(this.builtInIndex);
  }

  private tileCenter(col: number, row: number): Phaser.Math.Vector2 {
    return new Phaser.Math.Vector2(
      this.layout.left + col * (this.layout.tileSize + this.layout.gap) + this.layout.tileSize / 2,
      this.layout.top + row * (this.layout.tileSize + this.layout.gap) + this.layout.tileSize / 2
    );
  }

  private connectionEnd(x: number, y: number, direction: Direction): Phaser.Math.Vector2 {
    const vector = DIRECTION_VECTORS[direction];
    return new Phaser.Math.Vector2(
      x + vector.col * (this.layout.tileSize / 2),
      y + vector.row * (this.layout.tileSize / 2)
    );
  }

  private updateHud(message: string): void {
    movesElement.textContent = String(this.moves);
    attemptsElement.textContent = String(this.attempts);
    statusElement.textContent = message;
  }

  private setButtonsDisabled(disabled: boolean): void {
    sendButton.disabled = disabled;
    resetButton.disabled = disabled;
  }
}

const game = new Phaser.Game({
  type: Phaser.AUTO,
  width: GAME_WIDTH,
  height: GAME_HEIGHT,
  parent: "game",
  backgroundColor: "#0b1417",
  render: { antialias: true, pixelArt: false, roundPixels: true },
  scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
  scene: PacketPalScene
});

game.canvas.addEventListener("contextmenu", (event) => event.preventDefault());

const scene = (): PacketPalScene => game.scene.getScene("PacketPal") as PacketPalScene;

for (const [index, level] of LEVELS.entries()) {
  const option = document.createElement("option");
  option.value = String(index);
  option.textContent = `${index + 1} · ${level.title}`;
  levelSelect.append(option);
}

sendButton.addEventListener("click", () => scene().sendPackets());
resetButton.addEventListener("click", () => scene().resetLevel());
levelSelect.addEventListener("change", () => {
  if (levelSelect.value !== "custom") {
    scene().loadBuiltInLevel(Number.parseInt(levelSelect.value, 10));
  }
});
playAgainButton.addEventListener("click", () => {
  winDialog.close();
  scene().resetLevel();
});
nextLevelButton.addEventListener("click", () => {
  const index = scene().getBuiltInIndex();
  if (index !== null && index < LEVELS.length - 1) {
    winDialog.close();
    scene().loadBuiltInLevel(index + 1);
  }
});
winDialog.addEventListener("cancel", () => winDialog.close());

const PALETTE: { kind: EditorTool; label: string; symbol: string }[] = [
  { kind: "source", label: "Client", symbol: "PC" },
  { kind: "server", label: "Server", symbol: "▣" },
  { kind: "straight", label: "Line", symbol: "│" },
  { kind: "corner", label: "Corner", symbol: "└" },
  { kind: "router", label: "Router", symbol: "➜" },
  { kind: "oneway", label: "One-way", symbol: "→" },
  { kind: "cross", label: "Cross", symbol: "╳" },
  { kind: "hub", label: "Hub", symbol: "⊕" },
  { kind: "turnstile", label: "Rotator", symbol: "↻" },
  { kind: "encryptor", label: "Encrypt", symbol: "◆" },
  { kind: "firewall", label: "Firewall", symbol: "▥" },
  { kind: "filter", label: "Filter", symbol: "▽" },
  { kind: "erase", label: "Erase", symbol: "×" }
];

let selectedTool: EditorTool = "straight";
let selectedChannel: PacketChannel = "cyan";
let editorTiles: TileState[] = [];
let editorColumns = 7;
let editorRows = 5;

for (const channel of CHANNELS) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `channel-button${channel === selectedChannel ? " selected" : ""}`;
  button.style.setProperty("--channel-color", CHANNEL_CSS[channel]);
  button.setAttribute("aria-label", `${channel} packet channel`);
  button.addEventListener("click", () => {
    selectedChannel = channel;
    channelPalette.querySelectorAll(".channel-button").forEach((item, index) => {
      item.classList.toggle("selected", CHANNELS[index] === channel);
    });
  });
  channelPalette.append(button);
}

for (let columns = 6; columns <= 10; columns += 1) {
  const option = document.createElement("option");
  option.value = String(columns);
  option.textContent = String(columns);
  editorColumnsSelect.append(option);
}
for (let rows = 4; rows <= 7; rows += 1) {
  const option = document.createElement("option");
  option.value = String(rows);
  option.textContent = String(rows);
  editorRowsSelect.append(option);
}

for (const item of PALETTE) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `palette-button${item.kind === selectedTool ? " selected" : ""}`;
  button.dataset.tool = item.kind;
  button.innerHTML = `<span class="palette-symbol" aria-hidden="true">${item.symbol}</span>${item.label}`;
  button.addEventListener("click", () => {
    selectedTool = item.kind;
    tilePalette.querySelectorAll(".palette-button").forEach((paletteButton) => {
      paletteButton.classList.toggle(
        "selected",
        (paletteButton as HTMLElement).dataset.tool === selectedTool
      );
    });
  });
  tilePalette.append(button);
}

const editorTileSvg = (item: TileState): string => {
  const color = CHANNEL_CSS[item.channel ?? "cyan"];
  const wire =
    item.kind === "firewall"
      ? "#ff9a68"
      : item.kind === "turnstile"
        ? "#ffd76a"
      : item.kind === "encryptor"
        ? "#b79aff"
        : item.kind === "filter"
          ? color
          : "#62d7e8";
  const lines = getConnections(item)
    .map((direction) => {
      const ends: Record<Direction, [number, number]> = {
        0: [50, 0],
        1: [100, 50],
        2: [50, 100],
        3: [0, 50]
      };
      const [x, y] = ends[direction];
      return `<path d="M50 50L${x} ${y}" stroke="${wire}" stroke-width="8" stroke-linecap="round"/>`;
    })
    .join("");

  let center = '<circle cx="50" cy="50" r="16" fill="#142c32" stroke="#62d7e8" stroke-width="3"/>';
  if (item.kind === "source") {
    center = `<rect x="25" y="29" width="50" height="35" rx="6" fill="#142c32" stroke="${color}" stroke-width="4"/><path d="M40 73h20M50 64v9" stroke="${color}" stroke-width="4"/>`;
  } else if (item.kind === "server") {
    center = `<rect x="31" y="20" width="38" height="60" rx="6" fill="#11221e" stroke="${color}" stroke-width="4"/><circle cx="60" cy="34" r="3" fill="#ffd76a"/><circle cx="60" cy="50" r="3" fill="#ffd76a"/>`;
  } else if (item.kind === "router" || item.kind === "oneway") {
    const direction =
      item.kind === "oneway" ? (item.rotation + 1) % 4 : item.rotation;
    center = `<circle cx="50" cy="50" r="18" fill="#142c32" stroke="#62d7e8" stroke-width="3"/><path d="M37 50h26m-9-9 9 9-9 9" transform="rotate(${direction * 90} 50 50)" fill="none" stroke="#ffd76a" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`;
  } else if (item.kind === "turnstile") {
    center = `<circle cx="50" cy="50" r="18" fill="#142c32" stroke="#ffd76a" stroke-width="3"/><path d="M37 50h26m-9-9 9 9-9 9" transform="rotate(${item.rotation * 90} 50 50)" fill="none" stroke="#ffd76a" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`;
  } else if (item.kind === "cross") {
    center = '<circle cx="50" cy="50" r="13" fill="#142c32" stroke="#83f1c1" stroke-width="4"/>';
  } else if (item.kind === "hub") {
    center = '<rect x="33" y="33" width="34" height="34" rx="8" fill="#3a271c" stroke="#ff9a68" stroke-width="4"/>';
  } else if (item.kind === "encryptor") {
    center = '<rect x="31" y="32" width="38" height="34" rx="7" fill="#241f3d" stroke="#b79aff" stroke-width="4"/><path d="M40 32v-5a10 10 0 0 1 20 0v5" fill="none" stroke="#b79aff" stroke-width="4"/>';
  } else if (item.kind === "firewall") {
    center = '<rect x="29" y="29" width="42" height="42" rx="5" fill="#3a241d" stroke="#ff9a68" stroke-width="4"/><path d="M31 43h38M31 57h38M50 31v38" stroke="#ff9a68" stroke-width="3"/>';
  } else if (item.kind === "filter") {
    center = `<circle cx="50" cy="50" r="18" fill="#142229" stroke="${color}" stroke-width="4"/><path d="M39 40h22l-7 20h-8z" fill="none" stroke="${color}" stroke-width="3"/>`;
  }
  return `<svg viewBox="0 0 100 100" aria-hidden="true">${lines}${center}</svg>${
    item.fixed ? '<span class="cell-lock" aria-label="Locked">●</span>' : ""
  }`;
};

const setEditorStatus = (message: string, error = false): void => {
  editorStatus.textContent = message;
  editorStatus.classList.toggle("error", error);
};

const renderEditorGrid = (): void => {
  editorGrid.replaceChildren();
  editorGrid.style.setProperty("--editor-columns", String(editorColumns));
  for (let row = 0; row < editorRows; row += 1) {
    for (let col = 0; col < editorColumns; col += 1) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "editor-cell";
      const item = editorTiles.find((candidate) => candidate.col === col && candidate.row === row);
      button.setAttribute(
        "aria-label",
        item
          ? `${item.channel ? `${item.channel} ` : ""}${item.kind}, column ${col + 1}, row ${row + 1}`
          : `Empty, column ${col + 1}, row ${row + 1}`
      );
      if (item) button.innerHTML = editorTileSvg(item);
      button.addEventListener("click", () => editCell(col, row));
      button.addEventListener("contextmenu", (event) => {
        event.preventDefault();
        editorTiles = editorTiles.filter(
          (candidate) => candidate.col !== col || candidate.row !== row
        );
        renderEditorGrid();
      });
      editorGrid.append(button);
    }
  }
};

const editCell = (col: number, row: number): void => {
  const existing = editorTiles.find((item) => item.col === col && item.row === row);
  if (selectedTool === "erase") {
    editorTiles = editorTiles.filter((item) => item !== existing);
    renderEditorGrid();
    return;
  }

  const usesChannel = ["source", "server", "filter"].includes(selectedTool);
  if (
    existing?.kind === selectedTool &&
    (!usesChannel || existing.channel === selectedChannel)
  ) {
    existing.rotation = (existing.rotation + 1) % 4;
    existing.initialRotation = existing.rotation;
    existing.fixed = editorFixed.checked;
  } else {
    if (existing) editorTiles = editorTiles.filter((item) => item !== existing);
    editorTiles.push({
      col,
      row,
      kind: selectedTool,
      rotation: 0,
      initialRotation: 0,
      fixed: editorFixed.checked,
      channel: usesChannel ? selectedChannel : undefined
    });
  }
  setEditorStatus("Level changed. Save it or play it when ready.");
  renderEditorGrid();
};

const validateEditor = (): string | null => {
  const clients = editorTiles.filter((item) => item.kind === "source");
  const servers = editorTiles.filter((item) => item.kind === "server");
  if (clients.length === 0 || servers.length === 0) {
    return "Add at least one client and one server.";
  }
  for (const channel of CHANNELS) {
    const clientCount = clients.filter((item) => item.channel === channel).length;
    const serverCount = servers.filter((item) => item.channel === channel).length;
    if (clientCount > 0 && serverCount === 0) {
      return `${channel} clients need at least one ${channel} server.`;
    }
  }
  return null;
};

const normalizeSavedLevel = (value: unknown): SavedLevel => {
  if (!value || typeof value !== "object") throw new Error("Level data must be an object.");
  const candidate = value as Partial<SavedLevel>;
  const columns = Number(candidate.columns);
  const rows = Number(candidate.rows);
  if (
    typeof candidate.title !== "string" ||
    !Array.isArray(candidate.tiles) ||
    !Number.isInteger(columns) ||
    columns < 6 ||
    columns > 10 ||
    !Number.isInteger(rows) ||
    rows < 4 ||
    rows > 7
  ) {
    throw new Error("Level data needs a title, a 6–10 by 4–7 grid, and tiles.");
  }

  const occupied = new Set<string>();
  const tiles: TileState[] = candidate.tiles.map((raw) => {
    if (!raw || typeof raw !== "object") throw new Error("A tile is invalid.");
    const item = raw as Partial<TileState>;
    if (
      !Number.isInteger(item.col) ||
      !Number.isInteger(item.row) ||
      item.col === undefined ||
      item.row === undefined ||
      item.col < 0 ||
      item.col >= columns ||
      item.row < 0 ||
      item.row >= rows ||
      typeof item.kind !== "string" ||
      !TILE_KINDS.includes(item.kind as TileKind)
    ) {
      throw new Error("A tile is outside the grid or has an unknown type.");
    }
    const positionKey = `${item.col}:${item.row}`;
    if (occupied.has(positionKey)) throw new Error("Two tiles occupy the same square.");
    occupied.add(positionKey);
    const rotation =
      typeof item.rotation === "number" ? ((Math.round(item.rotation) % 4) + 4) % 4 : 0;
    const usesChannel = ["source", "server", "filter"].includes(item.kind);
    const channel = CHANNELS.includes(item.channel as PacketChannel)
      ? (item.channel as PacketChannel)
      : "cyan";
    return {
      col: item.col,
      row: item.row,
      kind: item.kind as TileKind,
      rotation,
      initialRotation: rotation,
      fixed: Boolean(item.fixed),
      channel: usesChannel ? channel : undefined
    };
  });
  return {
    title: candidate.title.trim().slice(0, 40) || "My level",
    columns,
    rows,
    tiles
  };
};

const loadEditorLevel = (saved: SavedLevel): void => {
  editorTitle.value = saved.title;
  editorColumns = saved.columns;
  editorRows = saved.rows;
  editorColumnsSelect.value = String(editorColumns);
  editorRowsSelect.value = String(editorRows);
  editorTiles = saved.tiles.map((item) => ({
    ...item,
    initialRotation: item.rotation,
    solutionRotation: undefined
  }));
  levelData.value = "";
  setEditorStatus("Level loaded.");
  renderEditorGrid();
};

const resizeEditor = (): void => {
  editorColumns = Number.parseInt(editorColumnsSelect.value, 10);
  editorRows = Number.parseInt(editorRowsSelect.value, 10);
  const before = editorTiles.length;
  editorTiles = editorTiles.filter(
    (item) => item.col < editorColumns && item.row < editorRows
  );
  setEditorStatus(
    before === editorTiles.length
      ? `Grid resized to ${editorColumns}×${editorRows}.`
      : "Grid resized; tiles outside the new edge were removed."
  );
  renderEditorGrid();
};

editorColumnsSelect.addEventListener("change", resizeEditor);
editorRowsSelect.addEventListener("change", resizeEditor);

editorButton.addEventListener("click", () => {
  const savedRaw = localStorage.getItem("packet-pal-custom-level-v2");
  if (savedRaw) {
    try {
      loadEditorLevel(normalizeSavedLevel(JSON.parse(savedRaw)));
    } catch {
      loadEditorLevel(scene().getCurrentLevelForEditor());
    }
  } else {
    loadEditorLevel(scene().getCurrentLevelForEditor());
  }
  editorDialog.showModal();
});

loadCurrentButton.addEventListener("click", () =>
  loadEditorLevel(scene().getCurrentLevelForEditor())
);

clearEditorButton.addEventListener("click", () => {
  editorTiles = [];
  editorTitle.value = "My level";
  levelData.value = "";
  setEditorStatus("Grid cleared. Add clients and matching servers.");
  renderEditorGrid();
});

saveLevelButton.addEventListener("click", () => {
  const error = validateEditor();
  if (error) {
    setEditorStatus(error, true);
    return;
  }
  const saved: SavedLevel = {
    title: editorTitle.value.trim().slice(0, 40) || "My level",
    columns: editorColumns,
    rows: editorRows,
    tiles: editorTiles
  };
  localStorage.setItem("packet-pal-custom-level-v2", JSON.stringify(saved));
  setEditorStatus("Saved in this browser.");
});

playCustomButton.addEventListener("click", () => {
  const error = validateEditor();
  if (error) {
    setEditorStatus(error, true);
    return;
  }
  editorDialog.close();
  scene().loadCustomLevel({
    id: "custom",
    title: editorTitle.value.trim().slice(0, 40) || "My level",
    tip: "Custom network · Deliver every client to a matching server.",
    columns: editorColumns,
    rows: editorRows,
    tiles: editorTiles.map((item) => ({
      ...item,
      initialRotation: item.rotation,
      solutionRotation: undefined
    }))
  });
});

exportLevelButton.addEventListener("click", () => {
  const saved: SavedLevel = {
    title: editorTitle.value.trim().slice(0, 40) || "My level",
    columns: editorColumns,
    rows: editorRows,
    tiles: editorTiles.map(({ col, row, kind, rotation, initialRotation, fixed, channel }) => ({
      col,
      row,
      kind,
      rotation,
      initialRotation,
      fixed,
      channel
    }))
  };
  levelData.value = JSON.stringify(saved);
  levelData.select();
  setEditorStatus("Level data exported below.");
});

importLevelButton.addEventListener("click", () => {
  try {
    loadEditorLevel(normalizeSavedLevel(JSON.parse(levelData.value)));
    setEditorStatus("Level data imported.");
  } catch (error) {
    setEditorStatus(error instanceof Error ? error.message : "Level data is invalid.", true);
  }
});

editorColumnsSelect.value = String(editorColumns);
editorRowsSelect.value = String(editorRows);
renderEditorGrid();
